import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../lib/supabase";
import { categoryGradients } from "../lib/constants";
import { CategoryIcon } from "../components/shared/CategoryIcon";
import { Search, MapPin, Star, Shield, MessageSquare, Clock, ChevronRight, PenTool, CheckCircle, ArrowRight } from "lucide-react";
import { useCountry } from "../contexts/CountryContext";
import { HeroMosaic } from "../components/shared/HeroMosaic";
import { AU_SUBURB_OPTIONS } from "../lib/country";

const HomePage = () => {
  const { country, countryCode } = useCountry();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const navigate = useNavigate();
  const isAU = countryCode === 'AU';
  const freeMonths = isAU ? '3 months' : '6 months';

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const loc = location.trim();
    if (loc) {
      // Carry the chosen suburb/city into the quote-request flow.
      try { sessionStorage.setItem('handyhub_prefill_location', loc); } catch { /* ignore */ }
    } else {
      try { sessionStorage.removeItem('handyhub_prefill_location'); } catch { /* ignore */ }
    }
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    if (loc) params.set('location', loc);
    navigate(`/services?${params.toString()}`);
  };

  return (
    <div className="home-page">
      {/* Hero Section - Dark Navy */}
      <section className="hero">
        <div className={`hero-content${isAU ? ' hero-content-mosaic' : ''}`}>
          <div className="hero-grid">
            <div className="hero-inner">
              <div className="hero-badge">
                <Shield size={14} />
                <span>Local Service Providers</span>
              </div>
              {isAU ? (
                <>
                  <h1 className="hero-h1-au">Find Local Services. <span className="hero-h1-highlight">Request Quotes with Ease.</span></h1>
                  <p className="hero-subtitle hero-subtitle-au">
                    Looking for a handyman, cleaner, painter, gardener or another service? Explore services, submit your quote request for free, and receive responses from available local service providers.
                  </p>
                </>
              ) : (
                <>
                  <h1>Find Trusted Local Service <span>Professionals</span> Near You</h1>
                  <p className="hero-subtitle">
                    Browse local provider profiles, compare ratings and reviews, and send quote requests directly. Look for the ID&nbsp;Verified badge on profiles that have completed verification.
                  </p>
                </>
              )}

              <form className="search-form" onSubmit={handleSearch} data-testid="search-form">
                <div className="search-input-wrapper">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="What service do you need?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
                <div className="search-input-wrapper">
                  <MapPin size={18} />
                  <input
                    type="text"
                    list={countryCode === 'AU' ? 'home-location-options' : undefined}
                    placeholder="Enter your suburb"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    autoComplete="off"
                    data-testid="search-location-input"
                  />
                  {countryCode === 'AU' && (
                    <datalist id="home-location-options">
                      {AU_SUBURB_OPTIONS.map(o => <option key={o} value={o} />)}
                    </datalist>
                  )}
                </div>
                <button type="submit" className="search-btn" data-testid="search-button">Search</button>
              </form>

              <div className="hero-stats">
                {isAU ? (
                  <>
                    <div className="hero-stat hero-stat-orange">
                      <MapPin size={14} />
                      <span>Local Services</span>
                    </div>
                    <div className="hero-stat hero-stat-orange">
                      <MessageSquare size={14} />
                      <span>Free Quote Requests</span>
                    </div>
                    <div className="hero-stat hero-stat-orange">
                      <CheckCircle size={14} />
                      <span>No Obligation</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="hero-stat amber">
                      <Star size={14} fill="currentColor" />
                      <span>Rated & Reviewed</span>
                    </div>
                    <div className="hero-stat green">
                      <CheckCircle size={14} />
                      <span>Ratings & Reviews</span>
                    </div>
                    <div className="hero-stat blue">
                      <Shield size={14} />
                      <span>ID Verified</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className={`hero-visual${isAU ? ' hero-visual-mosaic' : ''}`} data-testid="hero-visual">
              {isAU ? <HeroMosaic /> : (
              <div className="hero-trust-cards">
                <div className="hero-trust-card trust-card-1">
                  <div className="trust-card-icon trust-icon-amber">
                    <Shield size={20} />
                  </div>
                  <div className="trust-card-text">
                    <strong>Provider Profiles</strong>
                    <span>View provider information before choosing who to contact.</span>
                  </div>
                </div>
                <div className="hero-trust-card trust-card-2">
                  <div className="trust-card-icon trust-icon-green">
                    <Star size={20} fill="currentColor" />
                  </div>
                  <div className="trust-card-text">
                    <strong>Rated & Reviewed</strong>
                    <span>Transparent feedback</span>
                  </div>
                </div>
                <div className="hero-trust-card trust-card-3">
                  <div className="trust-card-icon trust-icon-blue">
                    <Clock size={20} />
                  </div>
                  <div className="trust-card-text">
                    <strong>Direct Quote Requests</strong>
                    <span>Send quote requests directly to service providers.</span>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>

          <div className="hero-provider-cta" data-testid="hero-provider-cta">
            <p>Are you a service professional?</p>
            <span>Grow your business with HandyHub {country.name}.</span>
            <div className="hero-provider-offer" data-testid="hero-provider-offer">
              <strong>Your first {freeMonths} free</strong>
              <ul>
                <li><CheckCircle size={14} /> Connect with local customers</li>
                <li><CheckCircle size={14} /> Receive quote requests</li>
                <li><CheckCircle size={14} /> No commissions on your jobs</li>
                <li><CheckCircle size={14} /> Keep 100% of what you earn</li>
              </ul>
            </div>
            <Link to="/signup?role=provider" className="btn-provider-cta" data-testid="hero-join-pro-btn">
              Get Started Free <ArrowRight size={16} />
            </Link>
            <small className="hero-provider-finetext">{freeMonths} free for new providers. After the free period, choose from monthly or yearly membership plans, starting from {isAU ? <>A$49&nbsp;+&nbsp;GST/month</> : 'NZ$49/month'}. T&amp;Cs apply.</small>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Popular Service Categories</h2>
            <p className="section-subtitle">{isAU ? 'Explore services by category and request free quotes from local service providers.' : 'Browse local providers by category'}</p>
          </div>
          
          <div className="categories-grid" data-testid="categories-grid">
            {categories.map((cat) => (
              <Link 
                to={`/categories/${cat.slug}`} 
                key={cat.id} 
                className="category-card"
                data-testid={`category-${cat.slug}`}
              >
                <div className={`category-icon ${categoryGradients[cat.slug] || 'cleaning'}`}>
                  <CategoryIcon iconName={cat.icon_name} />
                </div>
                <h3>{cat.name}</h3>
              </Link>
            ))}
          </div>

          <div className="section-cta">
            <Link to="/categories" className="btn-secondary" data-testid="view-all-categories">
              View All Categories <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* How HandyHub Works — Premium 3-Step Section */}
      <section className="how-it-works-premium" data-testid="how-it-works">
        <div className="section-container">
          <div className="section-header">
            <h2>How HandyHub Works</h2>
            <p className="section-subtitle">Get your job done in 3 simple steps</p>
          </div>
          
          <div className="steps-premium-grid">
            <div className="step-premium-card" data-testid="step-1">
              <div className="step-premium-icon">
                <PenTool size={26} />
              </div>
              <div className="step-premium-number">01</div>
              <h3>Tell us what you need</h3>
              <p>Submit a quick request in seconds</p>
            </div>
            <div className="step-connector" aria-hidden="true">
              <ChevronRight size={20} />
            </div>
            <div className="step-premium-card" data-testid="step-2">
              <div className="step-premium-icon">
                <MessageSquare size={26} />
              </div>
              <div className="step-premium-number">02</div>
              <h3>Get quotes from professionals</h3>
              <p>Reviewed providers respond with pricing</p>
            </div>
            <div className="step-connector" aria-hidden="true">
              <ChevronRight size={20} />
            </div>
            <div className="step-premium-card" data-testid="step-3">
              <div className="step-premium-icon">
                <CheckCircle size={26} />
              </div>
              <div className="step-premium-number">03</div>
              <h3>Choose and get the job done</h3>
              <p>Compare options and hire with confidence</p>
            </div>
          </div>

          <div className="steps-premium-cta" data-testid="steps-cta">
            <Link to="/categories" className="btn-primary btn-large" data-testid="browse-all-categories-btn">
              Browse All Categories <ChevronRight size={18} />
            </Link>
            <Link to="/quote-request/other" className="btn-secondary" data-testid="request-quote-btn">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      <section className="join-pro-section">
        <div className="section-container">
          <div className="join-pro-card">
            <div className="join-pro-content">
              <h2>Grow Your Local Service Business</h2>
              <ul className="provider-benefits-list" data-testid="provider-benefits">
                <li><CheckCircle size={16} /> Get quote requests from nearby customers</li>
                <li><CheckCircle size={16} /> Connect directly with local customers</li>
                <li><CheckCircle size={16} /> {country.trialLabelShort} free trial for new providers</li>
              </ul>
            </div>
            <Link to="/signup?role=provider" className="btn-primary btn-large" data-testid="join-as-pro">
              Join as a Local Pro <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Categories Page (grouped by category with services)

export default HomePage;
